import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import axios from "axios";
import { toast } from "sonner";
import {
  Car,
  ChevronRight,
  CheckCircle,
  Lock,
  TrendingUp,
  Target,
  Shield,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  1: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", accent: "amber" },
  2: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", accent: "blue" },
  3: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", accent: "emerald" },
};

export default function Assessment() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // If user already has assessment results, show them directly
    if (user?.assessment_completed && user?.assessment_result) {
      setResult(user.assessment_result);
      trackEvent("results_viewed");
      setLoading(false);
      return;
    }
    fetchQuestions();
    trackEvent("assessment_started");
  }, []);

  const fetchQuestions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/assessment/questions`, { withCredentials: true });
      setQuestions(data.questions);
    } catch {
      toast.error("Failed to load assessment");
    } finally {
      setLoading(false);
    }
  };

  const trackEvent = useCallback(async (event) => {
    try {
      await axios.post(`${API_URL}/api/assessment/track`, { event }, { withCredentials: true });
    } catch {
      // Silent
    }
  }, []);

  const handleSelect = (questionId, option, isMulti) => {
    if (isMulti) {
      const current = answers[questionId] || [];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      setAnswers({ ...answers, [questionId]: updated });
    } else {
      setAnswers({ ...answers, [questionId]: option });
      // Auto-advance after short delay for single-select
      setTimeout(() => {
        if (step < questions.length - 1) {
          setStep((s) => s + 1);
        }
      }, 250);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const answersList = questions.map((q) => ({
        question_id: q.id,
        answer: answers[q.id] || (q.multi ? [] : ""),
      }));
      const { data } = await axios.post(
        `${API_URL}/api/assessment`,
        { answers: answersList },
        { withCredentials: true }
      );
      setResult(data.result);
      trackEvent("results_viewed");
      await refreshUser();
    } catch {
      toast.error("Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const currentQ = questions[step];
  const isAnswered = currentQ && (
    currentQ.multi
      ? (answers[currentQ.id] || []).length > 0
      : !!answers[currentQ.id]
  );
  const allAnswered = questions.every((q) =>
    q.multi ? (answers[q.id] || []).length > 0 : !!answers[q.id]
  );
  const progressPct = questions.length > 0 ? Math.round(((step + 1) / questions.length) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  // RESULTS SCREEN
  if (result) {
    const colors = LEVEL_COLORS[result.level] || LEVEL_COLORS[2];
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50">
        <div className="max-w-sm mx-auto px-4 py-8 space-y-6">
          {/* Headline */}
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Car className="w-9 h-9 text-white" />
            </div>
            <h1 data-testid="result-headline" className="text-xl font-black text-slate-900 leading-tight">
              You're closer than you think... but a few things are holding you back
            </h1>
          </div>

          {/* Level Badge */}
          <div data-testid="result-level" className={`${colors.bg} ${colors.border} border rounded-2xl p-5 text-center`}>
            <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-1">Your Level</p>
            <p className={`text-2xl font-black ${colors.text}`}>{result.level_name}</p>
          </div>

          {/* Strength */}
          <div data-testid="result-strength" className="bg-white rounded-2xl border border-emerald-200 p-5 flex items-start gap-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wide mb-1">Your Strength</p>
              <p className="text-sm text-slate-900 font-medium">{result.strength}</p>
            </div>
          </div>

          {/* Weakness */}
          <div data-testid="result-weakness" className="bg-white rounded-2xl border border-amber-200 p-5 flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-amber-600 font-semibold uppercase tracking-wide mb-1">Key Area to Work On</p>
              <p className="text-sm text-slate-900 font-medium">{result.weakness}</p>
            </div>
          </div>

          {/* LOCKED SECTION — Upgrade Wall */}
          <div data-testid="locked-plan-section" className="relative rounded-2xl border border-slate-200 overflow-hidden">
            {/* Blurred locked content */}
            <div className="bg-white p-5 space-y-4 blur-[6px] select-none pointer-events-none" aria-hidden="true">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-400" />
                <p className="font-semibold text-slate-400">Your personalised step-by-step improvement plan</p>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-400">What to focus on in your next 3 lessons</p>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-400">How to speed up your progress</p>
              </div>
            </div>
            {/* Lock overlay */}
            <div className="absolute inset-0 bg-white/60 flex flex-col items-center justify-center px-6">
              <Lock className="w-8 h-8 text-blue-600 mb-3" />
              <p className="text-sm text-slate-600 font-medium text-center">Premium content — unlock to see your full plan</p>
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-3 pt-2">
            <Button
              data-testid="unlock-plan-btn"
              onClick={() => {
                trackEvent("upgrade_clicked_from_results");
                navigate("/upgrade");
              }}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl text-base"
            >
              Unlock Your Personal Pass Plan
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <p className="text-center text-xs text-slate-500">
              Most learners at your stage pass faster with a structured plan
            </p>
          </div>

          {/* Go to dashboard */}
          <button
            data-testid="result-dashboard-btn"
            onClick={() => navigate("/dashboard")}
            className="w-full text-sm text-slate-500 hover:text-slate-700 py-2 text-center"
          >
            Go to dashboard
          </button>
          <button
            data-testid="retake-assessment-btn"
            onClick={() => { setResult(null); setStep(0); setAnswers({}); fetchQuestions(); trackEvent("assessment_started"); }}
            className="w-full text-xs text-slate-400 hover:text-slate-600 py-1 text-center"
          >
            Retake assessment
          </button>
        </div>
      </div>
    );
  }

  // QUESTION FLOW
  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-sm mx-auto">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500 font-medium">
              Question {step + 1} of {questions.length}
            </p>
            <p className="text-sm font-semibold text-blue-600">{progressPct}%</p>
          </div>
          <Progress value={progressPct} className="h-2 bg-slate-100" />
        </div>
      </header>

      {/* Question */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          {currentQ && (
            <div>
              <h2
                data-testid="question-text"
                className="text-xl font-black text-slate-900 mb-6 leading-tight"
              >
                {currentQ.question}
              </h2>

              {currentQ.multi && (
                <p className="text-xs text-slate-500 mb-4 -mt-2">Select all that apply</p>
              )}

              <div className="space-y-3">
                {currentQ.options.map((option) => {
                  const selected = currentQ.multi
                    ? (answers[currentQ.id] || []).includes(option)
                    : answers[currentQ.id] === option;
                  return (
                    <button
                      key={option}
                      data-testid={`option-${option.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      onClick={() => handleSelect(currentQ.id, option, currentQ.multi)}
                      className={`w-full p-4 rounded-2xl border-2 text-left font-medium transition-all ${
                        selected
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-slate-200 hover:border-slate-300 text-slate-700"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 flex-shrink-0 flex items-center justify-center ${
                            currentQ.multi ? "rounded-md border-2" : "rounded-full border-2"
                          } ${
                            selected
                              ? "border-blue-600 bg-blue-600"
                              : "border-slate-300"
                          }`}
                        >
                          {selected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                        <span className="text-sm">{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-3 mt-8">
                {step > 0 && (
                  <Button
                    data-testid="prev-btn"
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Back
                  </Button>
                )}
                {currentQ.multi && step < questions.length - 1 && (
                  <Button
                    data-testid="next-btn"
                    onClick={() => setStep(step + 1)}
                    disabled={!isAnswered}
                    className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-40"
                  >
                    Next
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                )}
                {step === questions.length - 1 && (
                  <Button
                    data-testid="submit-btn"
                    onClick={handleSubmit}
                    disabled={!allAnswered || submitting}
                    className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-40"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Calculating...
                      </span>
                    ) : (
                      "Get My Results"
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Skip */}
      <div className="bg-white border-t border-slate-200 px-4 py-4">
        <div className="max-w-sm mx-auto text-center">
          <button
            data-testid="skip-assessment-btn"
            onClick={() => navigate("/dashboard")}
            className="text-sm text-slate-400 hover:text-slate-600"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
